import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hurricaneApi } from '../api';
import { hurricaneReport } from '../../../constants/routes';

function toLocalInputValue(d = new Date()) {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 16);
}

function HurricaneHome() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [locations, setLocations] = useState([]);
  const [residents, setResidents] = useState([]);
  const [events, setEvents] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [residentId, setResidentId] = useState('');
  const [hurricaneId, setHurricaneId] = useState('');
  const [inspectedAt, setInspectedAt] = useState(toLocalInputValue());
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([hurricaneApi.properties(), hurricaneApi.events()])
      .then(([propRes, evRes]) => {
        setProperties(propRes.data);
        setEvents(evRes.data);
        if (propRes.data.length === 1) setPropertyId(String(propRes.data[0].id));
        if (evRes.data.length === 1) setHurricaneId(String(evRes.data[0].id));
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!propertyId) {
      setLocations([]);
      setLocationId('');
      setResidents([]);
      setContext(null);
      return;
    }
    hurricaneApi.locations(propertyId).then((res) => setLocations(res.data)).catch(() => setLocations([]));
    setLocationId('');
    setResidentId('');
  }, [propertyId]);

  useEffect(() => {
    if (!locationId) {
      setResidents([]);
      setResidentId('');
      return;
    }
    hurricaneApi.residents(locationId).then((res) => {
      setResidents(res.data);
      if (res.data.length === 1) setResidentId(String(res.data[0].id));
    }).catch(() => setResidents([]));
  }, [locationId]);

  useEffect(() => {
    if (!propertyId || !locationId) {
      setContext(null);
      return;
    }
    hurricaneApi.context(propertyId, locationId)
      .then((res) => setContext(res.data))
      .catch(() => setContext(null));
  }, [propertyId, locationId]);

  const start = async (e) => {
    e.preventDefault();
    if (!propertyId || !hurricaneId) {
      setError('Select community and hurricane.');
      return;
    }
    setStarting(true);
    setError('');
    try {
      const res = await hurricaneApi.createReport({
        propertyId: Number(propertyId),
        locationId: locationId ? Number(locationId) : null,
        residentId: residentId ? Number(residentId) : null,
        hurricaneId: Number(hurricaneId),
        inspectedAt: new Date(inspectedAt).toISOString(),
      });
      navigate(hurricaneReport(res.data.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start report');
    } finally {
      setStarting(false);
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : '—');

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <div className="app-shell-page">
      <h2 className="h4 mb-3">Start hurricane report</h2>
      {error && <div className="alert alert-danger">{error}</div>}

      <form className="card app-card bg-white" onSubmit={start}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Community *</label>
              <select className="form-select" value={propertyId} onChange={(e) => setPropertyId(e.target.value)} required>
                <option value="">Select…</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Hurricane *</label>
              <select className="form-select" value={hurricaneId} onChange={(e) => setHurricaneId(e.target.value)} required>
                <option value="">Select…</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.name}{ev.season ? ` (${ev.season})` : ''}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Date of inspection *</label>
              <input
                type="datetime-local"
                className="form-control"
                value={inspectedAt}
                onChange={(e) => setInspectedAt(e.target.value)}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Site number</label>
              <select className="form-select" value={locationId} onChange={(e) => setLocationId(e.target.value)} disabled={!propertyId}>
                <option value="">— Select site —</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Primary resident or guest</label>
              <select className="form-select" value={residentId} onChange={(e) => setResidentId(e.target.value)} disabled={!locationId}>
                <option value="">— Optional —</option>
                {residents.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary mt-4" disabled={starting}>
            {starting ? 'Starting…' : 'Start hurricane report'}
          </button>
        </div>
      </form>

      {propertyId && locationId && context && (
        <div className="card app-card bg-white mt-3">
          <div className="card-header bg-white">Site history</div>
          <div className="card-body small">
            <p className="mb-2">
              <strong>Most recent report:</strong>{' '}
              {context.mostRecentReportDate ? formatDate(context.mostRecentReportDate) : '—'}
            </p>
            {context.previousReports?.length > 0 ? (
              <>
                <strong>Previous hurricane reports</strong>
                <ul className="mb-0 mt-1">
                  {context.previousReports.map((row) => (
                    <li key={row.id}>
                      {formatDate(row.inspected_at)} — {row.hurricane_name} — {row.outcome || row.status}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-muted mb-0">No previous hurricane reports for this site.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HurricaneHome;
